import _ from 'lodash';
import FilterRegistry from '../../page-framework/filter/FilterRegistry';
import { timeRangeFilterId } from '../../filters';
import { CUSTOM_ID } from '../../filters/time-range-filter/TimeRangeFilterConstants';
import { getUrlParamFromEncodedState } from '../../../utilities/FilterStateUrlUtilities';
import { getUrlParamFromState } from '../../../utilities/PageStateUrlUtilities';
import { DrillInLink } from '../../links/DrillInLink';

const emptySelection = {
    startPos: 0,
    endPos: 0,
};

function buildTargetUrl(row, selection) {
    const entityFilterStateService = FilterRegistry.getFilterStateService(selection.entityName);
    const timeRangeFilterStateService = FilterRegistry.getFilterStateService(timeRangeFilterId);
    const pageStateParams = getUrlParamFromState({ queryId: row.queryId });
    const encodedFilterState = {
        [selection.entityName]: entityFilterStateService.encode(selection),
        [timeRangeFilterId]: timeRangeFilterStateService.encode({
            id: CUSTOM_ID,
            start: row.beginTime,
            end: row.endTime,
        }),
    };
    const filterParams = getUrlParamFromEncodedState(encodedFilterState);
    return `/${selection.page}?${pageStateParams}&${filterParams}`;
}

/**
 * This renders a "multi-link" component from alarmList data. It is only used by the Alerts panel, and is not
 * generic enough (or have broad enough applicability) to be a DataTable column type.
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
export function AlertDescription(props) {
    const { row } = props;

    // Either of the two linkSelection objects can be missing or invalid. Get the linkSelection data into an array,
    // order by startPos (so we can work front-to-back on the description), and throw out any invalid ones.
    const linkSelections = [row?.linkSelection0 || emptySelection, row?.linkSelection1 || emptySelection];
    const orderedLinkSelections = _.sortBy(linkSelections, ['startPos']).filter((l) => l.startPos !== l.endPos);

    // Divide the description into segments of plain text or linked text. There can be from one segment (no links)
    // to 5 segments (plain-text interleaved with two links). Each sibling should have a different key.
    const desc = row?.desc;
    const segments = [];
    if (orderedLinkSelections.length === 0) {
        segments.push({ text: desc, key: 't0' });
    } else {
        const selection0 = orderedLinkSelections[0];
        const selection1 = orderedLinkSelections.length > 1 ? orderedLinkSelections[1] : null;
        if (selection0.startPos > 0) {
            segments.push({ text: desc.slice(0, selection0.startPos), key: 't0' });
        }
        segments.push({
            text: desc.slice(selection0.startPos, selection0.endPos),
            url: buildTargetUrl(row, selection0),
            key: 'l0',
        });
        if (selection1) {
            if (selection1.startPos > selection0.endPos) {
                segments.push({ text: desc.slice(selection0.endPos, selection1.startPos), key: 't1' });
            }
            segments.push({
                text: desc.slice(selection1.startPos, selection1.endPos),
                url: buildTargetUrl(row, selection1),
                key: 'l1',
            });
        }
        const endPos = selection1 ? selection1.endPos : selection0.endPos;
        if (endPos < desc.length) {
            segments.push({ text: desc.slice(endPos), key: 't2' });
        }
    }

    // Build the multi-link markup from the segments
    return (
        <>
            {segments.map((segment) =>
                segment.url ? (
                    <DrillInLink to={segment.url} key={segment.key}>
                        {segment.text}
                    </DrillInLink>
                ) : (
                    <span key={segment.key}>{segment.text}</span>
                )
            )}
        </>
    );
}

export function alertDescriptionRenderer(row) {
    return <AlertDescription row={row} />;
}
